import type { ReactNode } from 'react';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Card } from '@shared/ui/containers/Card';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/** Heading over a list of home cards (Explore groups, Customize home sections). */
export const listHeadingClass = 'px-1 text-base font-bold text-ink';

/**
 * Widget title row, above the widget's card: the title (+ optional subtitle)
 * with an optional action on the right — a text link, or a solid pill for the
 * widget's primary action (e.g. Invite).
 */
export function WidgetHeader({
  title,
  subtitle,
  actionLabel,
  actionIcon: ActionIcon,
  actionVariant = 'link',
  onAction,
}: {
  title: string;
  subtitle?: ReactNode;
  actionLabel?: string;
  actionIcon?: LucideIcon;
  actionVariant?: 'link' | 'solid';
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="flex min-h-8 items-center gap-3">
      <div className="min-w-0 flex-1">
        <MarqueeText as="h2" className="text-[1.125rem] font-bold leading-tight text-ink">{title}</MarqueeText>
        {subtitle ? <MarqueeText as="p" className="mt-0.5 text-caption text-ink-muted">{subtitle}</MarqueeText> : null}
      </div>

      {actionLabel && onAction ? (
        actionVariant === 'solid' ? (
          <button
            type="button"
            onClick={onAction}
            className="flex min-h-9 shrink-0 items-center gap-1 rounded-pill bg-primary-600 px-3.5 text-caption font-semibold text-primary-ink transition-transform active:scale-[0.97]"
          >
            {ActionIcon ? <ActionIcon className="h-3.5 w-3.5" strokeWidth={2.2} /> : null}
            {actionLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={onAction}
            className="flex shrink-0 items-center gap-0.5 py-1.5 text-body-sm font-semibold text-primary-700"
          >
            {ActionIcon ? <ActionIcon className="h-4 w-4" strokeWidth={2} /> : null}
            {actionLabel}
            {ActionIcon ? null : <ChevronRight className="h-4 w-4" strokeWidth={2} />}
          </button>
        )
      ) : null}
    </div>
  );
}

/**
 * Shared chrome for a home dashboard widget: the title row, then the widget
 * body on a card below it.
 */
export function WidgetCard({
  title,
  subtitle,
  actionLabel,
  actionIcon,
  actionVariant,
  onAction,
  onClick,
  children,
  className,
}: {
  title: string;
  subtitle?: ReactNode;
  actionLabel?: string;
  actionIcon?: LucideIcon;
  actionVariant?: 'link' | 'solid';
  onAction?: () => void;
  /**
   * Tapping the card body navigates to the widget's module. Interactive
   * children (buttons, hold-tiles) should call `e.stopPropagation()` so their
   * own taps don't also trigger this.
   */
  onClick?: () => void;
  children?: ReactNode;
  /** Extra classes for the card (e.g. its padding, or an alert ring). */
  className?: string;
}): JSX.Element {
  const tappable = !!onClick;
  return (
    <section className="flex flex-col gap-2.5">
      <WidgetHeader
        title={title}
        subtitle={subtitle}
        actionLabel={actionLabel}
        actionIcon={actionIcon}
        actionVariant={actionVariant}
        onAction={onAction}
      />

      {/* A widget with nothing to show (e.g. no upcoming visitors) is just its title row. */}
      {children ? (
        <Card
          noGrain
          className={cn(
            'flex flex-col gap-3 p-3.5',
            tappable && 'cursor-pointer transition-transform active:scale-[0.99]',
            className,
          )}
          {...(tappable
            ? {
                role: 'button',
                tabIndex: 0,
                onClick,
                onKeyDown: (e) => {
                  // Only the card itself — Enter on an inner button must
                  // activate that button, not be cancelled and rerouted here.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                  }
                },
              }
            : {})}
        >
          {children}
        </Card>
      ) : null}
    </section>
  );
}
